
import React, { createContext, useContext, ReactNode } from 'react';
import { User, Session, AuthContextType } from '@/types/auth';
import { useAuthState } from '@/hooks/auth/useAuthState';
import { useAuthentication } from '@/hooks/auth/useAuthentication';
import { useProfile } from '@/hooks/auth/useProfile';

// Create the auth context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Auth context hook
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Auth provider component
export const AuthProvider = ({ children }: { children: ReactNode }) => {
  // Auth state management
  const { 
    session, 
    user, 
    loading: stateLoading,
    setSession,
    setUser
  } = useAuthState();
  
  // Authentication methods
  const { 
    signIn: authSignIn, 
    signUp: authSignUp, 
    signOut: authSignOut, 
    loading: authLoading 
  } = useAuthentication();
  
  // Profile management
  const { 
    updateProfile: profileUpdate, 
    updating: profileUpdating 
  } = useProfile();

  // Wrap authentication functions to match expected return types
  const signIn = async (email: string, password: string): Promise<void> => {
    console.log('AuthProvider: Attempting sign in for', email);
    try {
      await authSignIn(email, password);
    } catch (error) {
      console.error('AuthProvider: Sign in error', error);
      throw error;
    }
  };

  const signUp = async (email: string, password: string): Promise<void> => {
    try {
      await authSignUp(email, password);
    } catch (error) {
      console.error('AuthProvider: Sign up error', error);
      throw error;
    }
  };

  const signOut = async (): Promise<void> => {
    try {
      await authSignOut();
    } catch (error) {
      console.error('AuthProvider: Sign out error', error);
      throw error;
    }
  };

  // Wrap the profile update function to include current state
  const updateProfile = async (data: { username?: string, avatar_url?: string }) => {
    if (!user) {
      throw new Error("User not authenticated");
    }
    return profileUpdate(user, data);
  };

  // Combine loading states
  const loading = stateLoading || authLoading || profileUpdating;

  // Create the context value
  const value: AuthContextType = {
    session,
    user,
    loading,
    signUp,
    signIn,
    signOut,
    updateProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
